import { useEffect, useState } from 'react';
import {
  Box, Button, Flex, HStack, Modal, ModalBody, ModalCloseButton, ModalContent, ModalOverlay,
  Spinner, Text, VStack, useToast,
} from '@chakra-ui/react';
import {
  useGetCreditsBalanceQuery, useGetCurrentSubscriptionQuery, useGetBillingPortalUrlMutation,
} from '../redux/services/mixgenie';
import { MG_GRADIENT } from '../theme';

// Reused inside the popup to decide whether to render "Manage billing"
// (only paying users have a Stripe billing portal to manage). Also
// exported from this file so callers can route the credits chip
// through the same signal.

/**
 * UserPopup — the account "sheet" that opens when a *paying* user taps
 * their credits chip or avatar in the composer. Free users get the
 * paywall instead (that split is wired by the parent — see
 * useIsPaidSub + the routeToUserOrPaywall helper below).
 *
 * Shape is intentionally close to pictools's profile popup so the two
 * products feel coherent for cross-shoppers: email + avatar + big
 * balance number + Plan/Status rows + Manage billing + Get more
 * credits + Log out. The footer links are kept to the three legally-
 * expected ones (Support / Privacy / Terms) + Refund Policy since the
 * sheet is also one of the convert-money surfaces.
 */
export function UserPopup({
  isOpen,
  onClose,
  email,
  onGetMoreCredits,
  onSignOut,
}: {
  isOpen: boolean;
  onClose: () => void;
  email: string;
  onGetMoreCredits: () => void;
  onSignOut: () => Promise<void> | void;
}) {
  const toast = useToast();
  const { data: creditsData, isLoading: balLoading } = useGetCreditsBalanceQuery(undefined as any, { skip: !isOpen });
  const { data: subData, isLoading: subLoading } = useGetCurrentSubscriptionQuery(undefined as any, { skip: !isOpen });
  const [openPortal, { isLoading: portalLoading }] = useGetBillingPortalUrlMutation();
  const [signingOut, setSigningOut] = useState(false);

  const bal = (creditsData as any)?.data || {};
  const credits = Number(bal.creditsRemaining ?? ((bal.currentPlan || 0) + (bal.purchased || 0))) || 0;
  const planCredits = Number(bal.currentPlan || 0);
  const purchasedCredits = Number(bal.purchased || 0);

  // Subscription metadata — same path PaywallModal uses to decide
  // isPaidSub, so the two surfaces agree.
  const subRaw = (subData as any)?.data;
  const planName = subRaw?.planName || bal.planName || (bal.plan === 'starter' ? 'MixGenie Free Signup' : bal.plan) || 'Free';
  const status = subRaw?.subscription?.status || subRaw?.status || bal.status || null;
  const renewalDate = subRaw?.subscription?.currentPeriodEnd || subRaw?.renewalDate || bal.renewalDate || null;
  // Paying = active Stripe sub. Free users don't see "Manage billing"
  // (nothing to manage until they convert; the Stripe billing portal
  // would 404 on a user with no customer id and the toast fallback is
  // noisy UX). The CTA to get them there lives below as "Get more
  // credits" which opens the paywall.
  const isPaidSub = !!(subRaw?.subscription?.status === 'active');

  const avatarInitial = (email[0] || 'U').toUpperCase();

  async function handleManageBilling() {
    try {
      const res: any = await openPortal().unwrap();
      const url = res?.url || res?.data?.url;
      if (url) { window.location.href = url; return; }
      toast({ title: 'Billing portal unavailable', status: 'warning', duration: 2500 });
    } catch (e: any) {
      toast({ title: 'Could not open billing portal', description: String(e?.data?.message || e?.message || e), status: 'error', duration: 3500 });
    }
  }

  async function handleSignOut() {
    try {
      setSigningOut(true);
      await onSignOut();
    } finally {
      setSigningOut(false);
      onClose();
    }
  }

  // Lock body scroll while open — matches the paywall sheet.
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered motionPreset="slideInBottom" size={{ base: 'full', md: 'md' }} autoFocus={false}>
      <ModalOverlay bg="blackAlpha.500" backdropFilter="blur(2px)" />
      <ModalContent
        bg="mg.surface"
        color="mg.fg"
        borderRadius={{ base: '16px 16px 0 0', md: '18px' }}
        mb={{ base: 0, md: 'auto' }}
        mt={{ base: 'auto', md: '10vh' }}
        maxW={{ base: '100%', md: '420px' }}
      >
        <ModalCloseButton color="mg.muted" top="10px" right="10px" _hover={{ bg: 'mg.surfaceHi' }} />
        <ModalBody px={{ base: 5, md: 6 }} py={{ base: 6, md: 7 }}>
          <VStack spacing={4} align="stretch">
            {/* Identity */}
            <VStack spacing={2}>
              <Box
                w="56px"
                h="56px"
                borderRadius="full"
                bgGradient={MG_GRADIENT}
                display="grid"
                placeItems="center"
                color="white"
                fontSize="22px"
                fontWeight={700}
              >
                {avatarInitial}
              </Box>
              <Text fontSize="15px" fontWeight={700} color="mg.fg" noOfLines={1} maxW="100%">
                {email || 'You'}
              </Text>
              {/* "Google account" subtitle is omitted — we don't reliably
                  know the provider on the FE yet. Add once the auth
                  provider is threaded through user data. */}
            </VStack>

            {/* Balance block */}
            <Box
              bg="mg.surfaceHi"
              border="1px solid"
              borderColor="mg.line"
              borderRadius="14px"
              p={5}
              textAlign="center"
            >
              {balLoading ? (
                <HStack justify="center"><Spinner size="sm" /></HStack>
              ) : (
                <>
                  <HStack justify="center" spacing={2} align="baseline">
                    <Text as="span" fontSize="22px" color="hot.500">⚡</Text>
                    <Text as="span" fontSize="40px" fontWeight={800} lineHeight={1} color="mg.fg">{credits}</Text>
                    <Text as="span" fontSize="13px" color="mg.muted" ml={1}>credits</Text>
                  </HStack>
                  <Text fontSize="12px" color="mg.muted" mt={2}>
                    {planCredits} plan · {purchasedCredits} purchased
                  </Text>
                </>
              )}
            </Box>

            {/* Plan / Status */}
            <Box bg="mg.surfaceHi" border="1px solid" borderColor="mg.line" borderRadius="14px" p={4}>
              <Flex align="center" justify="space-between" py={1}>
                <Text fontSize="13px" color="mg.muted">Plan</Text>
                <Text fontSize="13.5px" fontWeight={600} color="mg.fg">{subLoading ? '…' : planName}</Text>
              </Flex>
              <Flex align="center" justify="space-between" py={1}>
                <Text fontSize="13px" color="mg.muted">Status</Text>
                <Text fontSize="13.5px" fontWeight={700} color={status === 'active' ? 'green.400' : 'mg.muted'}>
                  {status || '—'}
                </Text>
              </Flex>
              {renewalDate && (
                <Flex align="center" justify="space-between" py={1}>
                  <Text fontSize="13px" color="mg.muted">Renews</Text>
                  <Text fontSize="13.5px" color="mg.fg">
                    {new Date(renewalDate).toLocaleDateString()}
                  </Text>
                </Flex>
              )}
              {isPaidSub && (
                <Button
                  variant="outline"
                  borderColor="mg.line2"
                  color="mg.fg"
                  _hover={{ bg: 'mg.surface' }}
                  size="sm"
                  mt={2}
                  w="full"
                  borderRadius="10px"
                  onClick={handleManageBilling}
                  isLoading={portalLoading}
                >
                  Manage billing
                </Button>
              )}
            </Box>

            {/* Primary CTA — stays on-sheet. Opens the paywall; parent
                closes the UserPopup first to avoid stacked modals. */}
            <Button
              size="lg"
              bgGradient={MG_GRADIENT}
              color="white"
              _hover={{ opacity: 0.92 }}
              borderRadius="12px"
              onClick={() => { onClose(); onGetMoreCredits(); }}
            >
              Get more credits
            </Button>

            <Button
              variant="ghost"
              color="mg.muted"
              _hover={{ color: 'mg.fg', bg: 'mg.surfaceHi' }}
              onClick={handleSignOut}
              isLoading={signingOut}
            >
              Log out
            </Button>

            {/* Legal + support footer */}
            <HStack spacing={4} justify="center" fontSize="11.5px" color="mg.dim" pt={1} wrap="wrap">
              <Box as="a" href="https://help.mixgenie.ai" target="_blank" rel="noopener noreferrer" _hover={{ color: 'mg.muted' }}>Support</Box>
              <Box as="a" href="/privacy" _hover={{ color: 'mg.muted' }}>Privacy</Box>
              <Box as="a" href="/terms" _hover={{ color: 'mg.muted' }}>Terms</Box>
              <Box as="a" href="/refund-policy" _hover={{ color: 'mg.muted' }}>Refund Policy</Box>
            </HStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

/**
 * useIsPaidSub — single source of truth for "does this user have a
 * real paid subscription". Matches the check PaywallModal already uses
 * so a user's status is the same regardless of which surface asks.
 *
 * Returns `null` while the first fetch is in flight — callers should
 * treat `null` as "not yet known" (default to the free-user path so
 * the user never gets stuck on a modal that doesn't apply).
 */
export function useIsPaidSub(enabled: boolean): boolean | null {
  const { data, isFetching } = useGetCurrentSubscriptionQuery(undefined as any, { skip: !enabled });
  if (!enabled) return false;
  if (isFetching && !data) return null;
  const d: any = data;
  // Primary signal: an active Stripe subscription attached.
  if (d?.data?.subscription?.status === 'active') return true;
  // Secondary signals the BE sometimes returns under different keys:
  if (d?.data?.status === 'active' && d?.data?.renewalDate) return true;
  if (d?.data?.plan && d?.data?.plan !== 'starter' && d?.data?.plan !== 'free') return true;
  return false;
}
